/**
 * device-capabilities.js — single source of truth for "what can this device run?".
 *
 * Wires together the scattered `navigator.deviceMemory`, `hardwareConcurrency`,
 * `connection.effectiveType`, `iosSafari` UA-sniffs that every tool was
 * re-implementing. Returns a frozen capability snapshot via `window.__deviceCap`
 * + a decision helper `canRunHeavyML(modelMB, opts)` for gate logic.
 *
 * Why a separate module (not inline in tool blades):
 *   1. Detection runs ONCE per page load, not per tool query.
 *   2. Future heavy-asset-manager + audio-engine-registry both depend on it.
 *   3. Any new heavy-ML feature can call `__deviceCap.canRunHeavyML(N, {...})`
 *      without re-implementing the gate; consistent UX across tools.
 *
 * Loaded WITHOUT defer (timbrica-core.js precedent) so inline tool scripts
 * can read it at parse time — but does no work until first .get() call.
 *
 * Telemetry hook: emits `__ae('device_cap_snapshot', {...})` on first read so
 * the dashboard can correlate feature-gate decisions with hardware cohorts.
 *
 * Cross-tool contract — DO NOT add platform-specific feature flags here that
 * would lock a tool into a single device class. This module reports facts;
 * gating decisions live with the caller (heavy-asset-manager, blade tool).
 */
(function(){
    'use strict';

    // Already loaded via duplicate <script> tag — bail.
    if (window.__deviceCap) return;

    var _cap = null;

    function detect() {
        var ua = navigator.userAgent || '';
        var dm = navigator.deviceMemory || 0;          // GB; spec caps at 8
        var hc = navigator.hardwareConcurrency || 0;
        var conn = navigator.connection || navigator.mozConnection || {};
        var effType = conn.effectiveType || '';
        var dl = conn.downlink || 0;                    // Mbps

        // iOS Safari detection — UA-sniff is the only reliable path. iPad with
        // desktop-mode UA still says Macintosh, so we add a touch+platform check.
        var isIOS = /iPad|iPhone|iPod/.test(ua) ||
                    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
        var isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(ua);
        // A HANDHELD — a phone or a tablet, whatever its browser claims. «Версия для ПК» /
        // «Request desktop site» (Chrome, Yandex, Samsung on Android) sends a Linux x86_64
        // user agent, so isMobile is false on those phones. Bug report 2026-09-28
        // (/ru/mashup-maker, Android 13, 4 GB, the tab died six times): in Chrome's
        // desktop mode the tool sized its memory as a computer's. The mode does not change
        // the hardware: a screen whose every pointer is a finger (a touch laptop still has
        // its trackpad — `any-pointer: fine`) and whose short side is phone- or tablet-sized.
        // A fact about the device class for memory and lane decisions; the page's LOOK keeps
        // following isMobile — the person asked for the desktop site.
        var isHandheld = isMobile || isIOS;
        if (!isHandheld) {
            try {
                var shortSide = Math.min(screen.width || 0, screen.height || 0);
                var mq = window.matchMedia;
                isHandheld = !!(mq && mq('(pointer: coarse)').matches && !mq('(any-pointer: fine)').matches
                    && (navigator.maxTouchPoints || 0) > 0 && shortSide > 0 && shortSide <= 1024);
            } catch (_) { isHandheld = false; }
        }
        // True iOS Safari (not Chrome/Firefox-on-iOS, both of which use WKWebView
        // but expose their own UA string under "CriOS"/"FxiOS").
        var isiOSSafari = isIOS && /^((?!chrome|crios|fxios|android).)*safari/i.test(ua);
        // Is the JS/wasm ENGINE JavaScriptCore? Broader than isiOSSafari on
        // purpose: desktop Safari is JSC too, and every browser on iOS is
        // WKWebView whatever it calls itself. Asked by callers that must avoid a
        // JSC-specific defect rather than an iOS-specific limit — e.g. ONNX
        // Runtime's JSEP build, which onnxruntime#26827 (open) shows sending the
        // WebKit process to 1 GB → 14 GB and getting it killed, while the plain
        // WASM build is fine. Measured here 2026-09-08 on WebKit 26.4 with the
        // same models: JSEP peaked at 1990 MB against 1216 MB for plain, a
        // 774 MB difference that does not exist on Chromium (945 vs 965).
        var isWebKit = /AppleWebKit\//.test(ua) &&
                       !/(Chrome|Chromium)\/|Edg\/|OPR\/|Firefox\//.test(ua);
        // A PHONE on WebKit (every iPhone browser). iPad says «Macintosh» and is
        // deliberately not here: for the ORT build it behaves like a Mac (see
        // __ortBuild's jsepOnDesktopWebKit below).
        var isPhoneWebKit = isWebKit && /iPhone|iPod/.test(ua);

        // Feature probes — cheap, no async work.
        var hasAudioWorklet = typeof AudioWorkletNode === 'function' &&
                              typeof AudioContext === 'function';
        // OffscreenCanvas + WebGPU are needed for some Demucs / Whisper paths.
        var hasOffscreenCanvas = typeof OffscreenCanvas === 'function';
        var hasWebGPU = !!(navigator.gpu && navigator.gpu.requestAdapter);
        var hasWebCodecs = typeof VideoDecoder === 'function';
        var hasOPFS = !!(navigator.storage && navigator.storage.getDirectory);
        var hasSAB = typeof SharedArrayBuffer !== 'undefined';
        // Web Speech API — used by ~14 voice-input tools. iOS Safari added it
        // only at 14.5; older iPads/iPhones can't be updated past iOS 12.5.x,
        // so this is the single most common "tool says nothing happens on tap"
        // root cause from old-Apple-device users.
        var hasSpeechRecognition = !!(window.SpeechRecognition || window.webkitSpeechRecognition);

        // iOS version — parsed for cutoff messages. iPad in "Request Desktop
        // Website" mode reports the macOS UA, so we only get a version when
        // the UA actually says iPad/iPhone/iPod. Two-tier: major.minor (12.5).
        // null = either non-iOS or iOS in desktop-UA mode.
        var iOSMajor = null;
        var iOSMinor = null;
        if (/iPad|iPhone|iPod/.test(ua)) {
            var m = /OS (\d+)_(\d+)(?:_(\d+))?/.exec(ua);
            if (m) {
                iOSMajor = parseInt(m[1], 10);
                iOSMinor = parseInt(m[2], 10);
            }
        }

        // SIMD probe is already in ffmpeg-processor.js as window.__hasWasmSIMD.
        // We re-expose its result here for one-stop access.
        var wasmSimd = (typeof window.__hasWasmSIMD === 'function')
            ? !!window.__hasWasmSIMD()
            : (typeof WebAssembly === 'object'); // best-effort fallback

        // Connection tier (used by heavy-asset-manager to skip auto-prompts on slow links).
        var connTier = 'unknown';
        if (effType === '4g' && dl >= 5)        connTier = 'fast';
        else if (effType === '4g')              connTier = 'medium';
        else if (effType === '3g')              connTier = 'slow';
        else if (effType === '2g' || effType === 'slow-2g') connTier = 'very-slow';

        // Composite tier — used for blanket "is this device capable" decisions.
        // Memory weighted heaviest because heavy ML is RAM-bound, not CPU.
        // Spec clamps deviceMemory at 8 GB; treat that as "high" automatically.
        //
        // ⚠️ Tiered on the BUDGET (window.__memBudget), not on the raw report.
        // Until 2026-08-31 the last branch fired whenever deviceMemory was
        // absent, and its own comment said "treat as low" while the value it
        // produced was 'unknown' — a string no caller tests for. WebKit never
        // reports deviceMemory, so `tier` was 'unknown' on 100% of iOS traffic
        // and all twelve `tier === 'low'` branches in the codebase (supertonic
        // precision, pocket-pet, tts-limits, text-to-speech, vr tests) were
        // dead on the one platform that needed them. A module that answers
        // "unknown" for a fifth of its callers is not a source of truth; it is
        // a fifth opinion, and every consumer goes back to inventing its own.
        var bgb = (window.__memBudget && window.__memBudget.gb) ? window.__memBudget.gb() : (dm || 4);
        var tier;
        if (bgb >= 8 && hc >= 4)        tier = 'high';
        else if (bgb >= 4 && hc >= 4)   tier = 'mid';
        else                            tier = 'low';

        return Object.freeze({
            memory: dm,                  // GB; 0 = the browser did not say
            budgetGB: bgb,               // GB you may budget against — never 0
            memoryAssumed: !dm,          // true when budgetGB is ours, not the browser's
            cores: hc,                   // 0 = unknown
            isIOS: isIOS,
            isMobile: isMobile,
            isHandheld: isHandheld,      // a phone/tablet even in «desktop site» mode (see above)
            isiOSSafari: isiOSSafari,
            isWebKit: isWebKit,          // JavaScriptCore — desktop Safari AND every iOS browser
            isPhoneWebKit: isPhoneWebKit, // an iPhone/iPod on WebKit (iPad reports as a Mac)
            iOSMajor: iOSMajor,          // null when non-iOS or iOS-in-desktop-UA
            iOSMinor: iOSMinor,
            hasAudioWorklet: hasAudioWorklet,
            hasOffscreenCanvas: hasOffscreenCanvas,
            hasWebGPU: hasWebGPU,
            hasWebCodecs: hasWebCodecs,
            hasOPFS: hasOPFS,
            hasSAB: hasSAB,
            hasSpeechRecognition: hasSpeechRecognition,
            wasmSimd: wasmSimd,
            connectionTier: connTier,
            connectionDownlink: dl,
            tier: tier,
            ua: ua                       // exposed for gates that need to render device-class strings
        });
    }

    /**
     * Decide whether to expose a heavy-ML feature on this device.
     *
     * Default policy (matches user choice 2026-05-08 «Скрыть полностью» on
     * weak devices — no advanced override on main UI):
     *
     *   - modelMB > device free RAM × 0.4   → false (OOM risk)
     *   - opts.requiresAudioWorklet && !hasAudioWorklet → false
     *   - opts.blockediOSSafari && isiOSSafari → false
     *   - opts.requiresWebGPU && !hasWebGPU → false
     *
     * Returns true when ALL gates pass. Caller can then offer the feature in UI.
     * On false, caller should NOT show the option (memory: «Скрыть полностью»),
     * NOT show it greyed-out — clean removal so user isn't tempted into OOM.
     *
     * @param {number} modelMB  approx in-memory cost of the model
     * @param {object} [opts]   {requiresAudioWorklet, blockediOSSafari, requiresWebGPU, requiresSAB, requiresWebCodecs}
     * @returns {boolean}
     */
    // Single read-point for the budget inside this module.
    function budgetGB() {
        if (window.__memBudget && window.__memBudget.gb) return window.__memBudget.gb();
        // Layout not loaded (unit test / bare page): fall back to the reported
        // number, and to 4 only when there is nothing at all to read.
        return (navigator.deviceMemory || 4);
    }

    function canRunHeavyML(modelMB, opts) {
        opts = opts || {};
        var c = _cap || (_cap = detect());

        // Memory gate — model RAM ≤ 40% of the budget. The budget comes from
        // window.__memBudget, which is the only place allowed to decide what an
        // absent deviceMemory means. This line used to say `c.memory || 4`,
        // i.e. it re-invented the answer one function below the module that is
        // supposed to own it — and on an iPhone that 4 GB let a 1638 MB model
        // through a tab that dies past ~600 MB.
        var memMB = budgetGB() * 1024;
        if (modelMB > memMB * 0.4) return false;

        if (opts.requiresAudioWorklet && !c.hasAudioWorklet) return false;
        if (opts.blockediOSSafari && c.isiOSSafari) return false;
        if (opts.requiresWebGPU && !c.hasWebGPU) return false;
        if (opts.requiresSAB && !c.hasSAB) return false;
        if (opts.requiresWebCodecs && !c.hasWebCodecs) return false;

        return true;
    }

    /**
     * One-time telemetry emit. Safe-wrapped so unhandled telemetry doesn't
     * propagate to feature code.
     */
    var _emitted = false;
    function get() {
        if (!_cap) _cap = detect();
        if (!_emitted && typeof window.__ae === 'function') {
            _emitted = true;
            try {
                window.__ae('device_cap_snapshot', {
                    dm: _cap.memory,
                    bgb: _cap.budgetGB,
                    hc: _cap.cores,
                    ios: _cap.isIOS ? 1 : 0,
                    iossafari: _cap.isiOSSafari ? 1 : 0,
                    mob: _cap.isMobile ? 1 : 0,
                    hh: _cap.isHandheld ? 1 : 0,
                    aw: _cap.hasAudioWorklet ? 1 : 0,
                    wg: _cap.hasWebGPU ? 1 : 0,
                    wc: _cap.hasWebCodecs ? 1 : 0,
                    opfs: _cap.hasOPFS ? 1 : 0,
                    sab: _cap.hasSAB ? 1 : 0,
                    simd: _cap.wasmSimd ? 1 : 0,
                    ct: _cap.connectionTier,
                    tier: _cap.tier
                });
            } catch(_) {}
        }
        return _cap;
    }

    // ── window.__ortBuild — the ONE answer to "which ONNX Runtime build" ─────
    //
    // `ort.min.js` references ONLY ort-wasm-simd-threaded.jsep.mjs. That build
    // is the subject of onnxruntime#26827 (open since 2025-12-18, no fix, no
    // workaround, `session.release()` does not help): on Safari/WebKit 26 it
    // puts the WebKit process at 400%+ CPU and 1 GB → 14 GB and gets it killed,
    // from a loop inside JSC's own wasm OMG compiler. The plain WASM build is
    // explicitly unaffected. Measured here on the same models, Δ peak working
    // set (tests/playwright/ort-bundle-on-webkit-bench.mjs, supertonic fp16):
    //
    //              JSEP      plain WASM
    //   WebKit     1990 MB     1216 MB     ← 774 MB
    //   Chromium    945 MB      965 MB     ← none; this is engine-specific
    //
    // ⚠️⚠️ This lives in ONE place because the alternative is already on the
    // disk and is the reason a Premium buyer lost his tab three times: the
    // string '/vendor/onnxruntime/ort.min.js' is hard-coded at **51 sites in 36
    // files**, so the answer to "which build" was re-decided, identically and
    // wrongly, thirty-six times. A number nobody owns is a number every new
    // module silently re-invents — the same lesson __memBudget was created for.
    // Callers ask here; workers get the string passed in (they cannot see
    // `window`). New ORT consumers must NOT write the path themselves —
    // OrtBuildSingleOwnerTest keeps the copy count from growing.
    //
    // ⚠️ The wasm-only build has no WebGPU EP: a caller that switches must also
    // stop asking for one, or it pays a failed createSession per session.
    var ORT_DIR = '/vendor/onnxruntime/';
    var ORT_VER = '1.24.3';
    //
    // ⚠️ `opts.jsepOnDesktopWebKit` — the one exception, and it is asked for, never
    // assumed. The rule above was measured on an iPhone-class failure and on a
    // desktop bench; production then showed it is a PHONE limit for some models,
    // not an engine one. The two stem separators (60 d to 24.09.2026, Safari):
    //   CascadedNet  Mac/iPad + JSEP/WebGPU 14 of 15 finished, 0.05–1.1× the song;
    //                iPhone   + JSEP/WebGPU  0 of 12 (the tab died);
    //   Kim          Mac/iPad + JSEP/WebGPU 49 split_ok, ~1.1×; wasm in one
    //                thread there is 8–14× the song;
    //                iPhone   + JSEP/WebGPU  9 ok of 116 loads — «[webgpu] Out of memory».
    // So a consumer that has MEASURED its model on desktop-class WebKit may keep
    // the JSEP build (and WebGPU) there; an iPhone never gets it. Consumers that
    // share window.ort on one page must pass the same answer — the first to load
    // the script decides for all of them.
    // ⚠️ Only WITH WebGPU. JSEP on WebKit without a GPU to run on is exactly
    // #26827: measured 24.09.2026 in WebKit 26 (Playwright, no WebGPU) — the
    // CascadedNet session cost +738 MB against +470 on the plain build, and
    // Kim's first chunk hung past its 120-s watchdog. The measured Macs all had
    // WebGPU; a Mac without it takes the plain build like an iPhone.
    //
    // ⚠️ `opts.jsepOnPhoneWebKit: '<engine key>'` — the phone exception, asked for
    // BY NAME and never without the death ledger below. «An iPhone never gets it»
    // was right for the stem separators and wrong for a small model: RVM
    // (video matting, 14 MB) ran on iPhone JSEP + WebGPU for three months before
    // 25.09.2026 at a median 302 ms/frame (n=20 matte passes, the fastest phone
    // lane on the site; Android WebGPU 793–1438, iPhone wasm 765), and reached
    // the review step as often as Android did (24 of 45 against 43–51 %). What
    // cannot be caught in-page is the #26827 death itself — WebKit kills the
    // process, no exception, no event. So the owner keeps a ledger per engine:
    // the consumer ARMS it before loading the build, SETTLES it once the model
    // has done real work, and a page that died in between leaves the mark behind
    // — the next load on this device reads it and takes the plain build for 30
    // days. A page that was HIDDEN when the mark was left is not blamed (iOS
    // discards background tabs for reasons of its own), and a normal pagehide
    // clears the mark. A catchable failure (`fail()`) is remembered the same way.
    // Only with WebGPU, exactly like the desktop exception.
    var PHONE_LEDGER_DAYS = 30;
    var _phoneVerdict = Object.create(null);   // key → 'ok' | 'died' (resolved once per page)
    var _phoneArmed = Object.create(null);
    function _ledgerKey(k) { return 'ort_jsep_phone:' + k; }
    function _ledgerRead(k) {
        try { return JSON.parse(localStorage.getItem(_ledgerKey(k)) || 'null'); } catch (_) { return null; }
    }
    function _ledgerWrite(k, v) {
        try {
            if (v == null) localStorage.removeItem(_ledgerKey(k));
            else localStorage.setItem(_ledgerKey(k), JSON.stringify(v));
        } catch (_) {}
    }
    function _ledgerTell(f, data) {
        // telemetry-tier0 may not be up yet when the verdict is read at load
        var send = function () {
            try { if (window.__aeFeature) { window.__aeFeature(f, data); return true; } } catch (_) {}
            return false;
        };
        if (!send()) setTimeout(send, 3000);
    }
    function phoneVerdict(k) {
        if (_phoneVerdict[k]) return _phoneVerdict[k];
        var rec = _ledgerRead(k), now = Date.now(), v = 'ok';
        if (rec && rec.s === 'died' && now - (rec.t || 0) < PHONE_LEDGER_DAYS * 864e5) {
            v = 'died';
        } else if (rec && rec.s === 'armed') {
            if (rec.hid) {
                _ledgerWrite(k, null);                 // cannot tell a crash from a discard
            } else {
                v = 'died';
                _ledgerWrite(k, { s: 'died', t: now, why: 'page' });
                _ledgerTell('ort_jsep_phone_died', { key: k, why: 'page', age_s: Math.round((now - (rec.t || now)) / 1000) });
            }
        } else if (rec) {
            _ledgerWrite(k, null);                     // expired death
        }
        return (_phoneVerdict[k] = v);
    }
    function _onHide() {
        for (var k in _phoneArmed) {
            var rec = _ledgerRead(k);
            if (rec && rec.s === 'armed' && !rec.hid) { rec.hid = 1; _ledgerWrite(k, rec); }
        }
    }
    function _onPageHide() {
        for (var k in _phoneArmed) {
            var rec = _ledgerRead(k);
            if (rec && rec.s === 'armed') _ledgerWrite(k, null);   // left normally
        }
    }
    var _phoneListeners = false;
    var phoneJsep = Object.freeze({
        /** Before the JSEP build loads on an iPhone. No-op elsewhere. */
        arm: function (k) {
            if (!k || !get().isPhoneWebKit) return;
            _phoneArmed[k] = 1;
            _ledgerWrite(k, { s: 'armed', t: Date.now(), hid: document.visibilityState === 'hidden' ? 1 : 0 });
            if (!_phoneListeners) {
                _phoneListeners = true;
                document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'hidden') _onHide(); });
                window.addEventListener('pagehide', _onPageHide);
            }
        },
        /** The model did real work on this page — the build is not killing it. */
        settle: function (k) {
            if (!k || !_phoneArmed[k]) return;
            delete _phoneArmed[k];
            var rec = _ledgerRead(k);
            if (rec && rec.s === 'armed') _ledgerWrite(k, null);
        },
        /** A catchable failure of the JSEP lane: this device takes the plain build next time. */
        fail: function (k, why) {
            if (!k || !get().isPhoneWebKit) return;
            delete _phoneArmed[k];
            _ledgerWrite(k, { s: 'died', t: Date.now(), why: String(why || 'error').slice(0, 60) });
            _phoneVerdict[k] = 'died';
            _ledgerTell('ort_jsep_phone_died', { key: k, why: String(why || 'error').slice(0, 60) });
        },
        /** 'ok' | 'died' — for telemetry. */
        verdict: function (k) { return k ? phoneVerdict(k) : 'ok'; }
    });
    // ⚠️ `opts.cpuOnly` — a WORKER that will only ever run the CPU (wasm) provider and loads
    // ORT for itself (importScripts), never window.ort on the page: it takes the plain build
    // on every engine. The JSEP wasm is twice the plain one (25 MB against 12): twice the
    // download on a phone, twice the compile, and on Chromium an idle ORT instance in a
    // worker held +240 MB of the tab against +170 on the plain build (measured 2026-09-30,
    // the /mashup-maker analysis worker, a one-node model). A pool of such workers paid
    // that difference per worker for a WebGPU provider none of them could use.
    function wasmBuild(opts) {
        if (opts && opts.cpuOnly) return true;
        var c = get();
        if (!c.isWebKit) return false;
        if (!c.hasWebGPU) return true;
        if (c.isPhoneWebKit) {
            return !(opts && opts.jsepOnPhoneWebKit && phoneVerdict(String(opts.jsepOnPhoneWebKit)) === 'ok');
        }
        return !(opts && opts.jsepOnDesktopWebKit);
    }
    // ── A CPU without WebAssembly SIMD → the LEGACY build (ORT 1.18.0) ─────────
    // onnxruntime-web dropped its non-SIMD wasm in 1.19: on a CPU whose V8 refuses
    // SIMD (no SSE4.1 — Core 2 before Penryn, Phenom II, Athlon II) the current
    // build has NO backend at all («WebAssembly SIMD is not supported»). 1.18.0 is
    // the last release with ort-wasm.wasm; we self-host it at LEGACY_DIR.
    // ⚠️ Its op set is older in ONE way that matters: ConvInteger takes uint8
    // weights only. A dynamic-int8 model with INT8 weights does not create on it
    // (measured 2026-10-10, DDColor: «Could not find an implementation for
    // ConvInteger(10)»; same for yamnet-int8); one with uint8 weights (GigaAM,
    // the Whisper encoders) does, MatMulInteger exists. Census 2026-10-11: 68 of
    // the 78 models we ship create a session on it. The dir has no threaded
    // wasm — a consumer keeps numThreads at 1 (pick() says so).
    // A browser with no WebAssembly at all (in-app browsers with wasm switched
    // off, Lockdown mode) is helped by no build: null there. ?ortlegacy=1 forces
    // the lane on normal hardware, for tests. bg-matting still decides this for
    // itself — migrate it here when touched.
    //
    // ── …and a browser that cannot RESERVE the current build's memory ──────────
    // Since 1.19 ORT ships only the threaded wasm, and that binary imports a
    // SHARED memory with a 4 GiB maximum. A shared memory cannot move, so the
    // engine reserves all of it up front — and WebKit before 18 cannot: every
    // session on every tool dies at init with «no available backend found. ERR:
    // [wasm] RangeError: Out of memory» (onnxruntime#22086). 30 days to
    // 2026-10-10, both shards: 194 sessions in 28 tools — iOS 16.4–17.7 (Safari
    // and every WKWebView browser), desktop Safari ≤ 17, now and then iOS 18+
    // when the tab's address space is already spent. 1.18's ort-wasm-simd.wasm
    // owns an ordinary, growing memory. /video-bg-remover has stepped down to it
    // on this failure since 2026-10-02 (6 loads in the field, Safari and Yandex
    // on iOS, all served) — this is that step for everybody.
    //
    // It is learned, not probed: reserving 4 GiB «to see» can itself be the
    // reservation that makes the real one fail. A consumer that catches the
    // failure tells the owner (initFailed), the shared error funnel does the same
    // for the consumers that do not, and from then on pick()/legacy() answer the
    // legacy build — for 30 days where the browser cannot ever do it (WebKit
    // < 18), for this tab only where it usually can (a transient failure must
    // not pin a healthy iPhone to the old runtime for a month).
    // ?ortnoshare=1 marks the page, for tests.
    var LEGACY_DIR = '/vendor/onnxruntime-legacy/', LEGACY_VER = '1.18.0';
    var NOSHARE_KEY = 'ort_noshare', NOSHARE_DAYS = 30;
    var _noShareHere = false;
    function _webkitMajor() {
        var ua = navigator.userAgent || '';
        var m = /\bOS (\d+)[_.]\d+/.exec(ua);                    // iPhone / iPad: every browser there is WKWebView
        if (m && /iPhone|iPad|iPod/.test(ua)) return +m[1];
        m = /Version\/(\d+)[\d.]* (?:Mobile\/\S+ )?Safari\//.exec(ua);   // desktop Safari (and iPadOS in desktop mode)
        return m ? +m[1] : 0;
    }
    function isReservationFailure(e) {
        var m = String((e && e.message) || e || '');
        // The text is ORT's own. «[webgpu] Out of memory» (a GPU buffer) has no
        // RangeError in it and is a different failure.
        return /RangeError/.test(m) && /out of memory/i.test(m) && /no available backend|initWasm|\[(?:wasm|webgpu)\]/i.test(m);
    }
    function noShare() {
        if (_noShareHere) return true;
        try { if (/[?&]ortnoshare=1/.test(window.location.search)) return true; } catch (_) {}
        try { if (sessionStorage.getItem(NOSHARE_KEY)) return true; } catch (_) {}
        try {
            var t = +localStorage.getItem(NOSHARE_KEY) || 0;
            if (t > 0 && Date.now() - t < NOSHARE_DAYS * 864e5) return true;
        } catch (_) {}
        return false;
    }
    function _markNoShare(from) {
        var already = noShare();
        _noShareHere = true;
        var wk = get().isWebKit ? _webkitMajor() : 0;
        var forGood = !!get().isWebKit && wk > 0 && wk < 18;
        try { sessionStorage.setItem(NOSHARE_KEY, '1'); } catch (_) {}
        if (forGood) { try { localStorage.setItem(NOSHARE_KEY, String(Date.now())); } catch (_) {} }
        if (!already) {
            _ledgerTell('ort_noshare', { from: String(from || '').slice(0, 40), wk: wk, keep: forGood ? 1 : 0 });
        }
    }
    function _wasmCompiles() {
        if (typeof WebAssembly !== 'object' || typeof WebAssembly.validate !== 'function') return false;
        // Present but forbidden to compile (in-app browsers) — no build helps.
        try { new WebAssembly.Module(new Uint8Array([0, 97, 115, 109, 1, 0, 0, 0])); } catch (_) { return false; }
        return true;
    }
    function legacyBuild() {
        if (!_wasmCompiles()) return null;
        var forced = false;
        try { forced = /[?&]ortlegacy=1/.test(window.location.search); } catch (_) {}
        var simd = !!get().wasmSimd;
        var why = forced ? 'forced' : (!simd ? 'nosimd' : (noShare() ? 'noshare' : ''));
        if (!why) return null;
        var path = 'npm/onnxruntime-web@' + LEGACY_VER + '/dist/ort.min.js';
        return {
            url: LEGACY_DIR + 'ort.min.js',
            wasmBase: LEGACY_DIR,
            fallbackUrl: (window.__LOCALE === 'ru')
                ? ((window.location && window.location.origin) || '') + '/cdn/jsdelivr/' + path
                : 'https://cdn.jsdelivr.net/' + path,
            version: LEGACY_VER,
            simd: simd,          // ort-wasm-simd.wasm is in the dir too; 'nosimd' is the slow lane
            why: why             // 'nosimd' | 'noshare' | 'forced'
        };
    }
    // The runtime binary each bundle fetches — for a consumer that downloads it
    // itself (to show its bytes on a progress bar) and hands it over as
    // ort.env.wasm.wasmBinary: the name belongs with the bundle, or the two drift
    // apart the day one of them moves (a JSEP binary under the plain bundle does
    // not instantiate). 1.18 picks by SIMD alone, its dir holds no threaded file.
    function _runtimeFile(legacy, simd, opts) {
        if (legacy) return simd ? 'ort-wasm-simd.wasm' : 'ort-wasm.wasm';
        return wasmBuild(opts) ? 'ort-wasm-simd-threaded.wasm' : 'ort-wasm-simd-threaded.jsep.wasm';
    }
    function _currentUrl(opts) { return ORT_DIR + (wasmBuild(opts) ? 'ort.wasm.min.js' : 'ort.min.js'); }
    function _currentFallback(opts) {
        var file = wasmBuild(opts) ? 'ort.wasm.min.js' : 'ort.min.js';
        var path = 'npm/onnxruntime-web@' + ORT_VER + '/dist/' + file;
        return (window.__LOCALE === 'ru')
            ? ((window.location && window.location.origin) || '') + '/cdn/jsdelivr/' + path
            : 'https://cdn.jsdelivr.net/' + path;
    }
    window.__ortBuild = Object.freeze({
        /** {url, wasmBase, fallbackUrl, version, simd, why} when this browser needs ORT 1.18, else null. */
        legacy: legacyBuild,
        /**
         * The build for a consumer whose models create on 1.18 (most do — see the
         * census above): ONE answer for the script, its CDN twin, the wasm dir, the
         * thread ceiling and whether a GPU provider is worth asking for.
         *   { url, fallbackUrl, wasmBase, legacy: ''|'nosimd'|'noshare'|'forced', threads: 0|1, webgpu, wasm }
         * `threads: 1` — the legacy dir has no threaded wasm; 0 = the consumer's own rule.
         * `wasm` — the runtime binary in wasmBase this bundle loads (see _runtimeFile).
         * opts are url()'s (cpuOnly, jsepOnDesktopWebKit, jsepOnPhoneWebKit).
         */
        pick: function (opts) {
            var L = legacyBuild();
            if (L) return { url: L.url, fallbackUrl: L.fallbackUrl, wasmBase: L.wasmBase, legacy: L.why, simd: L.simd, threads: 1, webgpu: false, wasm: _runtimeFile(true, L.simd) };
            return { url: _currentUrl(opts), fallbackUrl: _currentFallback(opts), wasmBase: ORT_DIR, legacy: '', simd: true, threads: 0, webgpu: !wasmBuild(opts), wasm: _runtimeFile(false, true, opts) };
        },
        /**
         * A consumer's ORT failed to start: was it the reservation failure, and is
         * there a build that does not need the reservation? true = the browser is
         * marked, pick()/legacy() answer the legacy build from now on, and the
         * caller should try once more in a FRESH runtime (a failed init is cached by
         * the one that failed). `used` — what the failed attempt ran on (the object
         * pick() returned, or its `.legacy`): a legacy attempt is never retried.
         */
        initFailed: function (err, used) {
            if (!isReservationFailure(err)) return false;
            var wasLegacy = !!(used && (typeof used === 'string' ? used : used.legacy));
            if (wasLegacy || !_wasmCompiles()) return false;
            _markNoShare(used && used.from);
            return true;
        },
        isReservationFailure: isReservationFailure,
        /** true once this browser is known not to reserve the current build's memory. */
        noShare: noShare,
        /**
         * The same answer for a worker that loads ORT by itself: a classic worker
         * imports the runtime on its first line, before any message can reach it,
         * and has no storage to ask — so the answer rides on the worker's own URL
         * (`ortb=legacy`), and the worker reads it from self.location.
         * `{simdOnly:true}` — the engine has no lane for a CPU without SIMD (never
         * measured there): such a CPU keeps today's answer.
         */
        workerUrl: function (url, opts) {
            var L = legacyBuild();
            if (!L || (opts && opts.simdOnly && !L.simd)) return url;
            url = String(url);
            if (/[?&]ortb=/.test(url)) return url;
            return url + (url.indexOf('?') < 0 ? '?' : '&') + 'ortb=legacy';
        },
        /** The death ledger behind `jsepOnPhoneWebKit` (see above). */
        phoneJsep: phoneJsep,
        /** true when this engine must avoid the JSEP build (and thus WebGPU). */
        wasmOnly: function (opts) { return wasmBuild(opts); },
        /** Same-origin bundle of the CURRENT build for this engine (pick() also knows the legacy one). */
        url: _currentUrl,
        /** CDN twin of url() — RU routes through our own proxy (RKN blocks CDNs). */
        fallback: _currentFallback,
        wasmBase: function () { return ORT_DIR; },
        // ── The NATIVE WebGPU provider build (ort.webgpu.min.js + *.asyncify.*) ──
        // A third build, asked for BY NAME and only by engines whose graph the
        // JSEP provider cannot run. First such engine: MUSION separate.onnx —
        // JSEP answers «[Mul] Mul_186 failed: Can't perform binary op on the given
        // tensors», the native provider runs it and agrees with the CPU reference
        // to 63–65 dB (measured 2026-09-19, Chrome 153). null on WebKit: the
        // engine-specific defect above was measured on the JSEP wasm, this build
        // was never measured there at all, and «not measured» is not «safe».
        nativeGpu: function () {
            if (get().isWebKit) return null;
            var file = 'ort.webgpu.min.js';
            var path = 'npm/onnxruntime-web@' + ORT_VER + '/dist/';
            var cdn = (window.__LOCALE === 'ru')
                ? ((window.location && window.location.origin) || '') + '/cdn/jsdelivr/' + path
                : 'https://cdn.jsdelivr.net/' + path;
            return { url: ORT_DIR + file, wasmBase: ORT_DIR, fallbackUrl: cdn + file, fallbackWasmBase: cdn };
        }
    });

    /* ── Можно ли позволить себе ЭТУ модель ПРЯМО СЕЙЧАС ────────────────────
     *
     * Вопрос про БАЙТЫ, которые придётся скачать, а не про скорость канала.
     * До 23.09.2026 у четырёх дверей (`/redub`, общий пикер Whisper,
     * speech-workspace, supertonic) лежала СВОЯ копия проверки, и все четыре
     * спрашивали только «какой сейчас канал». Модель, УЖЕ ЛЕЖАЩАЯ В КЭШЕ,
     * стоит ноль байт — а отказ всё равно приходил.
     *
     * Чего это стоило (обращение #6850, Премиум): 19.09 её канал был `4g` →
     * русскую речь распознал GigaAM, 40 реплик, расшифровка до 116-й секунды.
     * 22.09 тот же файл на `3g` → `connectionTier:'slow'` → МОЛЧА whisper
     * base: 25 реплик и хвост потерян на 97-й секунде. Наши же замеры разницы
     * движков: 5.3 % против 38.1 % ошибок слов на 3.5-минутном диалоге
     * (03.09.2026) и 3.8 / 28.6 по корпусу — то есть семикратное ухудшение,
     * выданное без единого слова. Модель при этом ЛЕЖАЛА У НЕЁ НА ДИСКЕ:
     * `onnx-models` в service worker держит её cache-first полгода.
     *
     * Правила теперь в ОДНОМ месте и разделены по природе:
     *   • память и процессор  — ВСЕГДА (кэш не уменьшает потребность в RAM);
     *   • канал и saveData    — ТОЛЬКО когда модели ещё нет на диске.
     *
     * `cachedNow(url)` синхронна и отвечает по уже известному: первый вызов
     * заводит асинхронную пробу и возвращает `null` («пока не знаю»). Дверь
     * может нарисоваться сразу по каналу, а когда проба ответит — перерисоваться
     * (см. `onModelCacheKnown`). Так решение не ждёт кэша и не врёт о нём.
     */
    var _cacheProbe = Object.create(null);     // url → true | false | Promise
    var _cacheWaiters = [];

    function _cacheKeyOf(url) { return String(url || '').split('#')[0]; }

    function cachedNow(url) {
        // Модель часто НЕ один файл (у whisper-small энкодер 92 МБ и декодер
        // 157 МБ). «Уже на диске» значит ВСЕ её файлы: один найденный кусок
        // не отменяет скачивания второго.
        if (Array.isArray(url)) {
            if (!url.length) return false;
            var unknown = false;
            for (var i = 0; i < url.length; i++) {
                var one = cachedNow(url[i]);
                if (one === false) return false;
                if (one === null) unknown = true;
            }
            return unknown ? null : true;
        }
        var k = _cacheKeyOf(url);
        if (!k) return false;
        var v = _cacheProbe[k];
        if (v === true || v === false) return v;
        if (!v) modelCached(k);                 // заводим пробу и отвечаем «не знаю»
        return null;
    }

    function modelCached(url) {
        var k = _cacheKeyOf(url);
        if (!k) return Promise.resolve(false);
        var v = _cacheProbe[k];
        if (v === true || v === false) return Promise.resolve(v);
        if (v) return v;
        var p = (function () {
            try {
                if (typeof caches === 'undefined' || !caches.match) return Promise.resolve(false);
                // ignoreSearch: версия ассета живёт в query, а в кэш SW кладёт
                // ответ по полному URL — без этого своя же копия не находится.
                return caches.match(k, { ignoreSearch: true })
                    .then(function (r) { return !!r; }, function () { return false; });
            } catch (_) { return Promise.resolve(false); }
        })().then(function (hit) {
            _cacheProbe[k] = !!hit;
            var ws = _cacheWaiters.slice();
            for (var i = 0; i < ws.length; i++) { try { ws[i](k, !!hit); } catch (_) {} }
            return !!hit;
        });
        _cacheProbe[k] = p;
        return p;
    }

    /** Подписка «проба кэша ответила» — дверь перерисовывается по факту. */
    function onModelCacheKnown(fn) { if (typeof fn === 'function') _cacheWaiters.push(fn); }

    /**
     * Сколько минут будет качаться `mb` мегабайт при ТЕКУЩЕМ соединении.
     * `downlink` груб (округляется до 25 кбит/с и упирается в ~10 Мбит/с), но
     * «примерно 20 минут» — качественно другой разговор, чем «медленное
     * соединение»: первое человек может решить, второе только принять. Нет
     * downlink — нет и числа (`0`), и дверь говорит без минут.
     */
    function downloadMinutes(mb) {
        var c = _cap || (_cap = detect());
        var dl = +c.connectionDownlink || 0;         // Мбит/с
        if (!(dl > 0) || !(mb > 0)) return 0;
        return Math.max(1, Math.round((mb * 8) / dl / 60));
    }

    /**
     * @returns {{ok:boolean, reason:string, cached:(boolean|null), soft:boolean, minutes:number}}
     *   reason: '' | 'memory' | 'device' | 'connection' | 'save_data'
     *
     * ⚠️ `soft` — граница, которую легко стереть и дорого восстановить:
     *   ЖЁСТКИЙ отказ (память, процессор) — возможность на этом устройстве не
     *     заработает, её честно СКРЫТЬ;
     *   МЯГКИЙ отказ (канал, экономия трафика) — возможность заработает, просто
     *     дорого стоит СЕЙЧАС. Такую отбирать нельзя: она меняет только
     *     УМОЛЧАНИЕ, а человек обязан видеть её и цену (`minutes`) и мочь
     *     выбрать. Стёртая граница и есть дефект #6850: сетевой отказ вёл себя
     *     как приговор устройства и уносил точность молча.
     */
    function modelAffordable(opts) {
        opts = opts || {};
        var mb = +opts.mb || 0;
        var cached = opts.url ? cachedNow(opts.url) : false;
        var no = function (reason, soft) {
            return { ok: false, reason: reason, cached: cached, soft: !!soft,
                     minutes: soft ? downloadMinutes(mb) : 0 };
        };
        try {
            if (window.__memBudget && window.__memBudget.constrained && window.__memBudget.constrained()) {
                return no('memory', false);
            }
            if (mb > 0 && !canRunHeavyML(mb, opts.ml || {})) {
                return no('device', false);
            }
            if (opts.minMemoryGB && ((typeof navigator !== 'undefined' && navigator.deviceMemory) || 8) < opts.minMemoryGB) {
                return no('device', false);
            }
            // Канал решает ТОЛЬКО судьбу загрузки. Файл уже здесь — вопроса нет.
            if (cached !== true) {
                if (navigator.connection && navigator.connection.saveData) {
                    return no('save_data', true);
                }
                var c = _cap || (_cap = detect());
                if (c.connectionTier === 'slow' || c.connectionTier === 'very-slow') {
                    return no('connection', true);
                }
            }
        } catch (_) {}
        return { ok: true, reason: '', cached: cached, soft: false, minutes: 0 };
    }

    // Public surface — frozen so callers don't accidentally mutate.
    window.__deviceCap = Object.freeze({
        get: get,
        canRunHeavyML: canRunHeavyML,
        modelAffordable: modelAffordable,
        modelCached: modelCached,
        cachedNow: cachedNow,
        onModelCacheKnown: onModelCacheKnown,
        // Convenience getters that don't trigger telemetry — use these in hot
        // paths where you just want a flag without a beacon.
        memory: function(){ return (_cap || detect()).memory; },
        tier:   function(){ return (_cap || detect()).tier; },
        is:     function(flag){ var c = _cap || detect(); return !!c[flag]; }
    });
})();
